import React from "react";
import ReactDOM from "react-dom";
import { Provider } from "react-redux";
import { Route, Switch, Router } from "react-router-dom";
import { ConnectedRouter } from "connected-react-router/immutable";
import { I18nextProvider } from "react-i18next";

import * as Sentry from "@sentry/react";
import { Integrations } from "@sentry/tracing";

import configureStore, { history } from "./store/store";
import i18n from "./lang/i18n";

import { Dashboards } from './pages/Dashboard/Dashboards';
import { Menu } from "./pages/Menu";
import Orders from "./pages/Orders/Orders";
import StaffOrders from "./pages/StaffOrders/Orders";
import KitchenOrders from "./pages/KitchenOrders/Orders";
import MobileApp from "./pages/MobileApp/MobileApp";
import MobilePreview from "./pages/MobilePreview/MobilePreview";
import SettingsDesign from "./pages/SettingsDesign/SettingsDesign";
import { Stores } from "./pages/Stores";
import { Sidebar } from "./pages/Sidebar/Sidebar";
import { MainSettings } from "./pages/Settings/MainSettings";
import { ModifierMain } from "./pages/Modifier/ModifierMain";
import {QueryClient, QueryClientProvider} from "@tanstack/react-query";
import {ReactQueryDevtools} from "@tanstack/react-query-devtools";
import 'react-toastify/dist/ReactToastify.css';
import {toast, ToastContainer} from "react-toastify";
import { Statements } from './pages/Admin/Statements';
import { Users } from './pages/Users';

const store = configureStore(/* provide initial state if any */);

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 300000,
            retry: 0,
            retryDelay: 2000,
            refetchOnWindowFocus: false,
            onError: (error) => {
                toast.error(error.response?.data?.message)
            }
        },
    },
});

if (process.env.NODE_ENV != "development") {
    Sentry.init({
        dsn: "https://ad56883581714d97b7fbec4c032b93a8@o559475.ingest.sentry.io/5769195",
        integrations: [new Integrations.BrowserTracing()],

        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        // We recommend adjusting this value in production
        tracesSampleRate: 1.0,
        environment: process.env.NODE_ENV,
    });
}

// Sidebar menu
const sidebarMenu = document.getElementById("sidebar");
if (sidebarMenu) {
    ReactDOM.render(
        <Provider store={store}>
            <QueryClientProvider client={queryClient}>
                <I18nextProvider i18n={i18n}>
                    <ConnectedRouter history={history}>
                        <Router history={history}>
                            <Sidebar />
                        </Router>
                    </ConnectedRouter>
                </I18nextProvider>
            </QueryClientProvider>
        </Provider>,
        sidebarMenu
    );
}

// General Settings
const settings = document.getElementById("x-settings");
if (settings) {
    ReactDOM.render(
        <Provider store={store}>
            <I18nextProvider i18n={i18n}>
                <ConnectedRouter history={history}>
                    <Router history={history}>
                        <Switch>
                            <Route exact path="/chain/settings" component={MainSettings}/>
                        </Switch>
                    </Router>
                </ConnectedRouter>
            </I18nextProvider>
        </Provider>,
        settings
    );
}

//restaurant POS
//restaurant POS
const restaurantRoot = document.getElementById("x-restaurant");
if (restaurantRoot) {
    ReactDOM.render(
        <Provider store={store}>
            <I18nextProvider i18n={i18n}>
                <ConnectedRouter history={history}>
                    <Router history={history}>
                        <Switch>
                            <Route exact path="/chain/restaurants/:restId/edit" component={Restaurant}/>
                        </Switch>
                    </Router>
                </ConnectedRouter>
            </I18nextProvider>
        </Provider>,
        restaurantRoot
    );
}

// admin
const adminRoot = document.getElementById("x-admin");
if (adminRoot) {
    ReactDOM.render(
        <Provider store={store}>
            <QueryClientProvider client={queryClient}>
                <I18nextProvider i18n={i18n}>
                    <ConnectedRouter history={history}>
                        <Router history={history}>
                            <Switch>
                                <Route path="/admin/transactions-statements" component={Statements}/>
                            </Switch>
                        </Router>
                    </ConnectedRouter>
                </I18nextProvider>
                <ReactQueryDevtools position="bottom-right" />
                <ToastContainer />
            </ QueryClientProvider>
        </Provider>,
        adminRoot
    );
}

//other
const menuRoot = document.getElementById("x-menu");
if (menuRoot) {
    ReactDOM.render(
        <Provider store={store}>
            <QueryClientProvider client={queryClient}>
                <I18nextProvider i18n={i18n}>
                    <ConnectedRouter history={history}>
                        <Router history={history}>
                            <Switch>
                                <Route path="/chain/stores" component={Stores}/>
                                <Route path="/chain/menu" component={Menu}/>
                                <Route path="/chain/users" component={Users}/>
                                <Route path="/chain/dashboard" component={Dashboards}/>
                                {/* <Route exact path="/chain/categories/:catId/dishes" component={Dish}/> */}
                                <Route exact path="/chain/orders" component={Orders}/>
                                <Route exact path="/staff/orders" component={StaffOrders}/>
                                <Route exact path="/kitchen/orders" component={KitchenOrders}/>
                                <Route exact path="/bar/orders" component={StaffOrders}/>
                                <Route exact path="/chain/app" component={MobileApp}/>
                                <Route exact path="/chain/design" component={SettingsDesign}/>
                                <Route exact path="/chain/settings" component={MainSettings}/>
                                <Route exact path="/api/app/:appId" component={MobilePreview}/>
                                <Route exact path="/chain/api/modifiers/categories/create" component={ModifierMain}/>
                            </Switch>
                        </Router>
                    </ConnectedRouter>
                </I18nextProvider>
                <ReactQueryDevtools position="bottom-right" />
                <ToastContainer />
            </ QueryClientProvider>
        </Provider>,
        menuRoot
    );
}
if (module.hot && process.env.NODE_ENV === "development") {
    module.hot.accept();
}
